import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Spinner } from '@hz-design-system/web-ui';
import { useI18nContext } from '@ecg-internal/js-react-i18n';
import { Bounds } from 'google-map-react';
import { toast } from 'react-toastify';

import { BAD_POST_CODE_RESPONSE, LOADING_STATUSES, MARKER_WIDTH } from '../../constants/map';
import GoogleMap from '../../../../components/Map/Map';
import Sidebar from '../../../../components/Sidebar/Sidebar';
import AdsList from '../../../../components/AdsList/AdsList';
import fetchListings from '../../repositories/getListings';
import getZoom from '../../../../utils/getZoom';
import useSearch from '../../../../hooks/useSearch';
import getMinDist from './utils/getMinDist';

import Classes from './Map.module.scss';
import getCenter from '../../repositories/getCenter';
import getClusters from '../../repositories/getClusters';
import { TAreaCluster } from 'types/Clusters';
import { TAd } from 'types/Listings';

type TMapAppProps = {
  initialMapCenter: {
    latitude: number;
    longitude: number;
  };
  initialPostcode: string;
  initialRadius: number;
};

const DEFAULT_LATITUDE = 52.2129919;
const DEFAULT_LONGITUDE = 5.2793703;
const dismissToast: () => void = () => toast.dismiss();

let toastNotification: any;

const ToastNotificationType = {
  INFO: 0,
  ERROR: 1,
  POSTCODE_ERROR: 2,
} as const;
type ToastNotificationType = (typeof ToastNotificationType)[keyof typeof ToastNotificationType];

async function showToastNotification(type: ToastNotificationType, t: (test: string) => string): Promise<void> {
  if (!toastNotification) {
    toastNotification = (await import(/* webpackChunkName: "toastNotification" */ '../Toast/ToastNotification'))
      .default;
  }

  if (!toastNotification) {
    return;
  }

  switch (type) {
    case ToastNotificationType.INFO:
      toastNotification({
        type: 'info',
        description: t('searchFeedback.noSearchResults'),
      });
      return;

    case ToastNotificationType.ERROR:
      toastNotification({
        type: 'error',
        description: t('searchFeedback.error'),
      });
      return;
    case ToastNotificationType.POSTCODE_ERROR:
      toastNotification({
        type: 'error',
        description: t('searchFeedback.postCodeError'),
      });
      return;
    default:
      toastNotification({
        type: 'error',
        description: t('searchFeedback.error'),
      });
      return;
  }
}

// If the user is logged in and has configured it's postcode or does a search using
// a postcode there will be a postcode cookie set that is used on the backend of
// this BFF to translate it to coordinates that can be used to set the initial center
// of the map.
const Map = ({ initialMapCenter, initialPostcode, initialRadius }: TMapAppProps) => {
  const { t } = useI18nContext();
  let initialCenter: TCoordinates;

  if (initialMapCenter) {
    initialCenter = {
      lat: initialMapCenter.latitude,
      lng: initialMapCenter.longitude,
    };
  } else {
    initialCenter = {
      lat: DEFAULT_LATITUDE,
      lng: DEFAULT_LONGITUDE,
    };
  }

  // Unfortunately google-map-react does not provide types
  // for the map for maps arguments of the onGoogleApiLoaded
  // handler so therefore it is typed as any.
  const [googleMapWidth, setGoogleMapWidth] = useState<number>();
  const [selectedAds, setSelectedAds] = useState<TAd[] | undefined>();
  const [selectedClusterIndex, setSelectedClusterIndex] = useState<number | null>(null);
  const [clusters, setClusters] = useState<TAreaCluster[]>();
  const [bounds, setBounds] = useState<Bounds | null>(null);
  const [status, setStatus] = useState<TLoadingStatus>(LOADING_STATUSES.IDLE);
  const [center, setCenter] = useState<TCoordinates>(initialCenter);
  const fetchingCenter = useRef(false);
  const settingRadius = useRef(false);
  const [searchQuery, setSearchQuery] = useState<TSearchQuery>({
    postcode: initialPostcode,
    radius: initialRadius || 0,
  });
  const previousPostcode = useRef<string | undefined>(initialPostcode);
  const previousRadius = useRef<number>(0);
  const initialZoom: number = getZoom(initialPostcode);
  const zoom: number = getZoom(searchQuery?.postcode);

  // query.postcode can change whenever a search with a postcode is fired.
  // When it does, we use it to retrieve the geographical center of that
  // postcode and update the position of the Map with it, which in turn
  // will fetch new clusters as a result of the bounds of the map changing.
  useEffect(() => {
    if (!searchQuery.postcode && previousPostcode.current === previousPostcode.current) {
      return;
    }

    dismissToast();
    fetchingCenter.current = true;
    setStatus(LOADING_STATUSES.LOADING);

    async function centerMap(): Promise<void> {
      const { response: centerResponse, error } = await getCenter({ postcode: searchQuery.postcode as string });
      if (centerResponse) {
        fetchingCenter.current = false;
        setCenter({
          lat: centerResponse.latitude,
          lng: centerResponse.longitude,
        });
      } else {
        if (error?.code === BAD_POST_CODE_RESPONSE) {
          void showToastNotification(ToastNotificationType.POSTCODE_ERROR, t);
        } else {
          void showToastNotification(ToastNotificationType.ERROR, t);
        }
      }
      setStatus(LOADING_STATUSES.IDLE);
      fetchingCenter.current = false;
    }

    void centerMap();

    previousPostcode.current = searchQuery.postcode;
  }, [searchQuery.postcode, t]);

  useEffect(() => {
    if (searchQuery.radius === previousRadius.current) {
      return;
    }

    settingRadius.current = true;
  }, [searchQuery.radius]);

  useEffect(() => {
    async function fetchClusters(): Promise<void> {
      if (!bounds || !googleMapWidth) {
        return;
      }

      // We return from this function if postcode or radius
      // have changed as we know that will update bounds
      // state which will call this function when that's done.
      if (fetchingCenter.current || settingRadius.current) {
        return;
      }

      dismissToast();
      setStatus(LOADING_STATUSES.LOADING);
      const widthInDegrees = bounds.ne.lng - bounds.nw.lng;

      const minDist: number = getMinDist(widthInDegrees, MARKER_WIDTH, googleMapWidth);

      const clustersResponse = await getClusters({
        bounds,
        minDist,
        query: searchQuery.query as string,
        postcode: searchQuery.postcode as string,
        distance: searchQuery.radius,
        categoryId: searchQuery.categoryId as number,
      });

      if (!clustersResponse?.length && searchQuery.query) {
        void showToastNotification(ToastNotificationType.INFO, t);
      }

      if (clustersResponse) {
        setClusters(clustersResponse);
      } else {
        void showToastNotification(ToastNotificationType.ERROR, t);
      }

      setStatus(LOADING_STATUSES.IDLE);
    }
    void fetchClusters();
  }, [t, bounds, googleMapWidth, searchQuery.radius, searchQuery.postcode, searchQuery.query, searchQuery.categoryId]);

  useSearch(setSearchQuery);

  function handleClickMap(): void {
    setSelectedAds(undefined);
    setSelectedClusterIndex(null);
  }

  async function handleClickMarker(clusterIndex: number): Promise<void> {
    if (!clusters) {
      return;
    }

    dismissToast();
    const listings = await fetchListings(clusters[clusterIndex].ids);
    if (!listings) {
      void showToastNotification(ToastNotificationType.ERROR, t);
    }

    setSelectedAds(listings);
    setSelectedClusterIndex(clusterIndex);
  }

  const handleDoneSettingRadius = useCallback(() => {
    settingRadius.current = false;
  }, []);

  function handleGoogleApiLoaded({ width }: { width: any }) {
    setGoogleMapWidth(width);
  }

  return (
    <>
      <GoogleMap
        markers={clusters?.map((cluster) => ({
          ...cluster,
          count: cluster.ids.length,
        }))}
        defaultCenter={initialCenter}
        defaultZoom={initialZoom}
        center={center}
        zoom={zoom}
        setBounds={setBounds}
        onClickMap={handleClickMap}
        onClickMarker={handleClickMarker}
        options={{ draggable: true }}
        radius={searchQuery.radius}
        selectedMarkerIndex={selectedClusterIndex}
        onDoneSettingRadius={handleDoneSettingRadius}
        onGoogleApiLoaded={handleGoogleApiLoaded}
      />
      {status === LOADING_STATUSES.LOADING ? (
        <div data-testid="progress-bar" className={Classes.progressBar}>
          <Spinner />
        </div>
      ) : null}
      {selectedAds ? (
        <Sidebar>
          <AdsList ads={selectedAds} />
        </Sidebar>
      ) : null}
    </>
  );
};

export default Map;
